import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';

import { getClinicalNotes } from 'api/ClinicalNote';

import { formatDateAndTimeZ, ia, iaRa } from 'lib/helpers/utility';

import { currentPractice } from 'components/practice/practiceState';

import { isToday } from '../../../PreviewNote/lib/renderedHelper';
import SelectNoteTable from '../SelectNoteTable';

const ClinicalNotesBase = ({ setNote = () => {}, note = {}, queryType, setActive = {} }) => {
  const { id: patientId } = useParams();
  const parsedPatientId = parseInt(patientId);
  const navigate = useNavigate();
  const today = new Date();
  const startDate = queryType === 'upcoming' ? today : null;
  const endDate = queryType === 'upcoming' ? null : today;

  const clinicalNotesParams = {
    patientId: parsedPatientId,
    limit: 10,
    startDate,
    endDate,
    pagination: true,
    disableClamp: true,
    ...(queryType === 'upcoming' && { sort: 'ASC' })
  };
  const currPractice = useRecoilValue(currentPractice);

  const timezone = currPractice?.timezone;
  const scrollableDivRef = useRef(null);

  const { data, fetchNextPage, isFetching, isLoading } = useInfiniteQuery(
    ['clinicalNotesInfinite', parsedPatientId, queryType],
    ({ pageParam = { navigate, clinicalNotesParams } }) =>
      getClinicalNotes(pageParam.navigate, pageParam.clinicalNotesParams),
    {
      enabled: !!parsedPatientId,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      staleTime: 60 * 1000 * 5
    }
  );

  const allClinicalNotes = useMemo(
    () =>
      ia(data?.pages)
        ? data.pages
            ?.map((entry) => entry?.clinicalNote)
            .filter((cn) => ia(cn))
            .flat()
        : [],
    [data]
  );

  useEffect(() => {
    if (queryType === 'upcoming' && !isLoading && !ia(allClinicalNotes)) {
      setActive('past');
    }
  }, [isLoading]);

  const formatNoteLabel = (note, timezone) => {
    const date = formatDateAndTimeZ(note?.appointment?.starts_at, timezone);
    const provider = note?.practitioner?.professionalFullName;

    return `${date} - ${provider}`;
  };

  const getTodayNotes = (notes, timezone) =>
    iaRa(notes)?.filter((note) => isToday(note?.appointment?.starts_at, timezone));

  const todaysNoteOptions = useMemo(
    () =>
      getTodayNotes(allClinicalNotes, timezone).map((note) => ({
        label: formatNoteLabel(note),
        value: note?.appointment?.id,
        data: note
      })),
    [allClinicalNotes, timezone]
  );

  const noteOptions = useMemo(() => {
    return iaRa(allClinicalNotes)?.map((note) => ({
      label: formatNoteLabel(note),
      value: note?.appointment?.id,
      data: note
    }));
  }, [allClinicalNotes, timezone]);

  useEffect(() => {
    if (!note?.value && ia(todaysNoteOptions)) {
      const initialNote = ia(todaysNoteOptions) ? todaysNoteOptions[0] : null;
      if (initialNote) {
        setNote(initialNote);
      }
    }
  }, [noteOptions, todaysNoteOptions]);

  useEffect(() => {
    if (note?.value && scrollableDivRef.current) {
      const selectedElement = scrollableDivRef.current.querySelector(
        `[data-note-id="${note.value}"]`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [note?.value, queryType]);

  const lastPage = data?.pages?.[data.pages.length - 1];
  const lastPageNotes = ia(lastPage?.clinicalNote) ? lastPage.clinicalNote : [];
  const hasMore = lastPageNotes.length === clinicalNotesParams?.limit;

  const fetchNextPageWithCustomOffset = (offset) => {
    fetchNextPage({
      pageParam: {
        navigate,
        clinicalNotesParams: {
          ...clinicalNotesParams,
          offset
        }
      }
    });
  };

  const handleScroll = () => {
    const div = scrollableDivRef.current;
    if (div) {
      if (div.scrollTop + div.clientHeight >= div.scrollHeight - 5 && !isFetching) {
        if (hasMore) fetchNextPageWithCustomOffset(allClinicalNotes?.length);
      }
    }
  };

  const handleNoteSelect = (selectedNote) => {
    setNote(selectedNote);
  };

  return (
    <div>
      <SelectNoteTable
        noteOptions={noteOptions}
        scrollableDivRef={scrollableDivRef}
        handleScroll={handleScroll}
        selectedNote={note}
        onNoteSelect={handleNoteSelect}
        loading={isLoading}
        isFetching={isFetching}
        hasMore={hasMore}
      />
    </div>
  );
};

export default ClinicalNotesBase;
